/* wtf-cancer-free-date.js
 *
 * Month / Day / Year dropdowns for the Cancer-Free "Cancer-free date" line-item property.
 * Built 2026-10-12 (Jeff: Cancer-Free line live tonight; Noelle writes the month with no leading zero, R590).
 * Updated 2026-10-12 (Jeff R630: "no leading zeros anywhere to keep consistency"): the value that reaches the cart and
 * the print is always M.D.YY, with NO leading zero in the month and NO leading zero in the day.
 * Updated 2026-10-12 late (Jeff, terminal: "we need to make it drop down selections. A text box allows mistakes to happen
 * and the drop down will format it correctly."): the free-text box is gone. Three required dropdowns replace it.
 *
 * HOW THE VALUE REACHES THE CART. The three dropdowns have no name. One hidden input,
 * name="properties[Cancer-free date]" (the same property name as before), holds the print value. This script writes
 * that hidden input: M.D.YY when Month, Day and Year are all chosen and make a real date,
 * and the empty string otherwise. assets/wtf-teams-validate.js (unchanged) does the rest, exactly as before:
 *   - mirrors every [data-wtf-teams] properties[...] field into the add-to-cart form,
 *   - blocks add to cart while a required field is empty,
 *   - disables the Shop Pay / Buy it now button until the field is filled in.
 * This file adds the dropdown logic and a friendlier block: the first empty dropdown gets focus. The block listens on
 * `window` in the capture phase, which runs ahead of the teams script's `document` capture listeners. When the date is
 * complete, nothing is stopped here and the teams script mirrors the value as it always did.
 *
 * DROPDOWN RULES.
 *   Month: January to December, value 1 to 12.
 *   Day:   1 to the real length of the chosen month and year (28, 29, 30 or 31, leap years included). Before a month is
 *          chosen the list shows 1 to 31. With a month but no year, February shows 29. A chosen day that no longer fits
 *          (31 then April) is cleared, and the dropdown shows "Day" again.
 *   Year:  10 years ahead back to 1950, newest first, built from the customer's own clock each time the page loads.
 *   Future: allowed (see the 2026-10-24 update below).
 * The preview line under the boxes shows exactly what will be printed: "Your personalized item will read: 3.4.22" (Jeff's words, 10-24).
 * Updated 2026-10-24 (Jeff, terminal: "We want any date to be allowed. Futrue dates included in case someone wants to
 *   put a future date that they are expecting."): future dates are allowed. Nothing is disabled or cleared for being
 *   in the future. The year list runs from FUTURE_YEARS years ahead back to 1950.
 * check() is the one rule book: it takes "M.D.YYYY" built from the dropdowns and returns the M.D.YY print value.
 * It still accepts the other typed forms (3/4/22, 2022-03-04) because the tests and any pasted value rely on it.
 */
(function () {
  'use strict';

  var ERR = 'wtf-teams__input--error';
  /* a, b, c = the three parts. Separator must be the same twice. Length rules are checked in check(). */
  var RX = /^([0-9]{1,4})([.\/-])([0-9]{1,2})\2([0-9]{1,4})$/;
  var MSG_EMPTY = 'Please fill in Cancer-free date before adding to cart.';
  var MSG_FORMAT = 'Enter the date as Month.Day.Year, for example 3.4.22.';
  var MSG_REAL = 'That date is not on the calendar. Check the month and day.';
  var MSG_YEAR = 'Check the year. Use two digits (22) or four digits (2022).';

  function trim(s) {
    return String(s == null ? '' : s).replace(/^\s+|\s+$/g, '');
  }

  function real(y, m, d) {
    var dt = new Date(y, m - 1, d);
    return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d ? dt : null;
  }

  /* Pure check. Returns { ok: boolean, empty: boolean, msg: string, value: string }.
     `value` is the print format M.D.YY (no leading zero in month or day) and is set only when ok is true.
     `now` is optional, for tests. */
  function check(raw, now) {
    var v = trim(raw);
    if (v === '') return { ok: false, empty: true, msg: MSG_EMPTY, value: '' };
    var p = RX.exec(v);
    if (!p) return { ok: false, empty: false, msg: MSG_FORMAT, value: '' };
    var ms, ds, ys;
    if (p[1].length === 4) {
      /* year first: 2022-03-04 */
      ys = p[1];
      ms = p[3];
      ds = p[4];
      if (ds.length > 2) return { ok: false, empty: false, msg: MSG_FORMAT, value: '' };
    } else {
      ms = p[1];
      ds = p[3];
      ys = p[4];
      if (ms.length > 2 || (ys.length !== 2 && ys.length !== 4)) {
        return { ok: false, empty: false, msg: ys.length === 1 || ys.length === 3 ? MSG_YEAR : MSG_FORMAT, value: '' };
      }
    }
    if (ys.length !== 2 && ys.length !== 4) return { ok: false, empty: false, msg: MSG_YEAR, value: '' };
    var m = parseInt(ms, 10);
    var d = parseInt(ds, 10);
    if (!(m >= 1 && m <= 12) || !(d >= 1 && d <= 31)) return { ok: false, empty: false, msg: MSG_FORMAT, value: '' };
    var y = parseInt(ys, 10);
    if (ys.length === 4 && y < 1900) return { ok: false, empty: false, msg: MSG_YEAR, value: '' };
    if (ys.length === 2) y = 2000 + y;
    var dt = real(y, m, d);
    if (!dt) return { ok: false, empty: false, msg: MSG_REAL, value: '' };
    return { ok: true, empty: false, msg: '', value: m + '.' + d + '.' + ys.slice(-2) };
  }

  function daysInMonth(m, y) {
    if (!m) return 31;
    if (!y) return m === 2 ? 29 : new Date(2001, m, 0).getDate();
    return new Date(y, m, 0).getDate();
  }

  if (typeof window !== 'undefined') window.wtfCancerFreeDate = { check: check, daysInMonth: daysInMonth };
  if (typeof document === 'undefined') return;

  var FIRST_YEAR = 1950;
  var FUTURE_YEARS = 10;
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  var shown = false;

  function box() {
    return document.querySelector('[data-wtf-cf-date]');
  }
  /* The hidden input that carries properties[Cancer-free date]. */
  function input() {
    return document.querySelector('[data-wtf-cf-input]');
  }
  function pick(kind) {
    return document.querySelector('[data-wtf-cf-' + kind + ']');
  }
  function selects() {
    return [pick('month'), pick('day'), pick('year')].filter(function (s) {
      return !!s;
    });
  }

  function today() {
    var n = new Date();
    return { y: n.getFullYear(), m: n.getMonth() + 1, d: n.getDate() };
  }

  /* Fixed 2026-10-24 (customer could not add 2.19.1998): never re-check the hidden M.D.YY value. Its 2-digit year
     reads as 20YY, so every year 1950-1999 looked like the future and was blocked. Check the dropdowns instead,
     with the full 4-digit year. */
  function current() {
    var m = pick('month');
    var d = pick('day');
    var y = pick('year');
    var raw = m && d && y && m.value && d.value && y.value ? m.value + '.' + d.value + '.' + y.value : '';
    return check(raw);
  }

  /* The preview line shows exactly what will be printed. Hidden while the date is not complete and valid. */
  function preview() {
    var el = document.querySelector('[data-wtf-cf-preview]');
    var inp = input();
    if (!el || !inp) return;
    var r = current();
    if (r.ok) {
      el.textContent = 'Your personalized item will read: ' + r.value;
      el.hidden = false;
    } else {
      el.textContent = '';
      el.hidden = true;
    }
  }

  function note() {
    var b = box();
    if (!b) return null;
    var id = b.id + '-wtf-error';
    var el = document.getElementById(id);
    if (!el) {
      el = document.createElement('p');
      el.id = id;
      el.className = 'wtf-teams__error';
      el.setAttribute('role', 'alert');
      el.hidden = true;
      b.appendChild(el);
    }
    return el;
  }

  function clearStickyBar(n, field) {
    var bar = document.querySelector('.wtf-ff-sticky-atc--visible');
    var limit = window.innerHeight;
    if (bar) {
      var bt = bar.getBoundingClientRect().top;
      if (bt > 0 && bt < limit) limit = bt;
    }
    var r = n.getBoundingClientRect();
    var f = field.getBoundingClientRect();
    var down = r.bottom + 12 - limit;
    var up = f.top - 70;
    var s = 0;
    if (down > 0) s = down;
    else if (up < 0) s = Math.max(up, down);
    if (!s) return;
    try {
      window.scrollBy({ top: s, left: 0, behavior: 'instant' });
    } catch (e) {
      window.scrollBy(0, s);
    }
  }

  /* Show a message under the dropdowns. `focusEl` gets focus (no scroll jump) and the sticky bar is cleared;
     `marks` are the dropdowns that get the red border. */
  function paint(msg, focusEl, marks) {
    var n = note();
    if (!n) return;
    (marks || []).forEach(function (s) {
      s.classList.add('wtf-cf-date__select--error');
      s.setAttribute('aria-invalid', 'true');
    });
    n.textContent = msg;
    n.hidden = false;
    shown = true;
    if (focusEl) {
      try {
        focusEl.focus({ preventScroll: true });
      } catch (e) {
        focusEl.focus();
      }
      clearStickyBar(n, focusEl);
    }
  }

  function unpaint() {
    var inp = input();
    var n = note();
    shown = false;
    selects().forEach(function (s) {
      s.classList.remove('wtf-cf-date__select--error');
      s.removeAttribute('aria-invalid');
    });
    if (inp) {
      inp.classList.remove(ERR);
      inp.removeAttribute('aria-invalid');
    }
    if (n) {
      n.hidden = true;
      n.textContent = '';
    }
  }

  function stop(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
  }

  function emptySelects() {
    return selects().filter(function (s) {
      return s.value === '';
    });
  }

  /* Block the event when the date is not complete and valid. The first empty dropdown gets focus. */
  function guard(e) {
    var inp = input();
    if (!inp) return;
    refresh(true);
    var r = current();
    if (r.ok) return;
    stop(e);
    var empties = emptySelects();
    /* One tap on a phone fires pointerdown then touchstart (and maybe mousedown): count it once. */
    var nowMs = Date.now();
    if (nowMs - lastBlocked > 700) {
      lastBlocked = nowMs;
      track('wtf_cf_date_blocked', { reason: empties.length ? 'empty' : 'invalid', filled: filledCount() });
    }
    var all = selects();
    paint(empties.length ? MSG_EMPTY : r.msg, empties[0] || all[0] || null, empties.length ? empties : all);
  }

  /* Keep Shop Pay / Buy it now off while the date is empty or invalid. The teams script clears its own
     disabled state on every input event, so this runs after it and re-applies when the date is not valid. */
  function gate() {
    var inp = input();
    if (!inp) return;
    if (current().ok) return;
    Array.prototype.forEach.call(
      document.querySelectorAll('.shopify-payment-button, [data-shopify="payment-button"]'),
      function (w) {
        w.style.opacity = '0.45';
        w.style.pointerEvents = 'none';
        w.setAttribute('aria-disabled', 'true');
        w.setAttribute('title', 'Pick your cancer-free date above first');
      }
    );
  }

  /* Tracking (Jeff 2026-10-24: "if we can place click tracking in the drop-downs let's do it so we can monitor people
     moving forward"). Shopify customer events: Shopify.analytics.publish sends a custom event to the store's web pixels.
     It never blocks the page: any error is swallowed. Events:
       wtf_cf_date_pick     a dropdown changed: { field, filled: how many of the 3 are chosen, complete, year }
       wtf_cf_date_complete the 3 dropdowns first make a valid date on this page view: { year, future }
       wtf_cf_date_blocked  add to cart / Shop Pay was stopped: { reason: empty | invalid, filled } */
  var completeSent = false;
  var lastBlocked = 0;
  function track(name, data) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, data || {});
      }
    } catch (e) {
      /* tracking must never break the date picker */
    }
  }
  function filledCount() {
    return selects().filter(function (s) {
      return s.value !== '';
    }).length;
  }
  function yearChosen() {
    var y = pick('year');
    return y && y.value ? parseInt(y.value, 10) : null;
  }

  /* Make `sel` hold the placeholder (index 0) plus exactly `n` real options. */
  function ensureOptions(sel, n) {
    while (sel.options.length > n + 1) sel.remove(sel.options.length - 1);
    while (sel.options.length < n + 1) sel.appendChild(document.createElement('option'));
  }

  function setOption(o, value, text, disabled) {
    o.value = String(value);
    o.textContent = String(text);
    o.disabled = !!disabled;
  }

  /* Rebuild the three lists from the customer's clock and the current choices, then write the hidden input.
     Order: year list, month list, day list (month length), then the value.
     `quiet` is true when called from a block, where the caller paints its own message. */
  function refresh(quiet) {
    var mSel = pick('month');
    var dSel = pick('day');
    var ySel = pick('year');
    var inp = input();
    if (!mSel || !dSel || !ySel || !inp) return;
    var t = today();
    var msg = '';
    var i;

    /* Year: FUTURE_YEARS ahead back to FIRST_YEAR, newest first. Future dates are allowed (Jeff 10-24). */
    var lastY = t.y + FUTURE_YEARS;
    var keepY = parseInt(ySel.value, 10) || 0;
    ensureOptions(ySel, lastY - FIRST_YEAR + 1);
    for (i = 0; i <= lastY - FIRST_YEAR; i++) setOption(ySel.options[i + 1], lastY - i, lastY - i, false);
    var y = keepY >= FIRST_YEAR && keepY <= lastY ? keepY : 0;
    ySel.value = y ? String(y) : '';

    /* Month: all twelve, always enabled. */
    var keepM = parseInt(mSel.value, 10) || 0;
    ensureOptions(mSel, 12);
    for (i = 1; i <= 12; i++) setOption(mSel.options[i], i, MONTHS[i - 1], false);
    var m = keepM >= 1 && keepM <= 12 ? keepM : 0;
    mSel.value = m ? String(m) : '';

    /* Day: 1 to the real month length, always enabled. A day that no longer fits (31, then April) is cleared. */
    var keepD = parseInt(dSel.value, 10) || 0;
    var len = daysInMonth(m, y);
    ensureOptions(dSel, len);
    for (i = 1; i <= len; i++) setOption(dSel.options[i], i, i, false);
    var d = keepD >= 1 && keepD <= len ? keepD : 0;
    dSel.value = d ? String(d) : '';

    /* The value: M.D.YY only for a complete, real date. */
    var val = '';
    if (y && m && d) {
      var r = check(m + '.' + d + '.' + y);
      if (r.ok) val = r.value;
    }
    if (inp.value !== val) {
      inp.value = val;
      try {
        inp.dispatchEvent(new Event('input', { bubbles: true }));
      } catch (e) {
        /* old browser without the Event constructor: the guards still read the hidden input on the tap */
      }
    }
    preview();
    if (!quiet) {
      if (msg) paint(msg, null, null);
      else if (shown) unpaint();
    }
    gate();
  }

  function isAddTarget(e) {
    return !!(
      e.target &&
      e.target.closest &&
      e.target.closest('button[name="add"], .product-form__submit, .wtf-sticky-atc__btn, [data-wtf-sticky-atc]')
    );
  }

  function isChoice(e) {
    return !!(e.target && e.target.matches && e.target.matches('[data-wtf-cf-select]'));
  }

  function init() {
    if (!box() || !input() || !pick('month') || !pick('day') || !pick('year')) return;

    /* BLOCK FIRST. `window` capture listeners run before every `document` capture listener, including the teams
       script's, so an incomplete date is stopped here with focus on the first empty dropdown. */
    window.addEventListener(
      'click',
      function (e) {
        if (isAddTarget(e)) guard(e);
      },
      true
    );
    window.addEventListener(
      'submit',
      function (e) {
        if (e.target && e.target.matches && e.target.matches('form[action*="/cart/add"]')) guard(e);
      },
      true
    );

    /* Backstop on `document`, same checks as the first version that the judge verified. */
    document.addEventListener(
      'submit',
      function (e) {
        if (e.target && e.target.matches && e.target.matches('form[action*="/cart/add"]')) guard(e);
      },
      true
    );
    document.addEventListener(
      'click',
      function (e) {
        if (isAddTarget(e)) guard(e);
      },
      true
    );

    ['change', 'input'].forEach(function (n) {
      document.addEventListener(n, function (e) {
        if (!isChoice(e)) return;
        refresh(false);
        /* one event per real change: 'change' only (iOS fires both input and change on a select) */
        if (n !== 'change') return;
        var ok = current().ok;
        var t = e.target;
        track('wtf_cf_date_pick', {
          field: t.hasAttribute('data-wtf-cf-month') ? 'month' : t.hasAttribute('data-wtf-cf-day') ? 'day' : 'year',
          filled: filledCount(),
          complete: ok,
          year: yearChosen()
        });
        if (ok && !completeSent) {
          completeSent = true;
          var y = yearChosen();
          var td = today();
          var mm = parseInt(pick('month').value, 10);
          var dd = parseInt(pick('day').value, 10);
          var fut = y > td.y || (y === td.y && (mm > td.m || (mm === td.m && dd > td.d)));
          track('wtf_cf_date_complete', { year: y, future: fut });
        }
      });
    });

    /* Back / forward cache and browser autofill can restore the dropdowns without a change event. */
    window.addEventListener('pageshow', function () {
      refresh(false);
    });

    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () {
        pending = null;
        gate();
      }, 50);
    }).observe(document.querySelector('product-info') || document.body, { childList: true, subtree: true });

    /* Last chance before Shop Pay / Buy it now reads the form: block an incomplete date. */
    ['pointerdown', 'touchstart', 'mousedown'].forEach(function (n) {
      document.addEventListener(
        n,
        function (e) {
          if (!e.target || !e.target.closest) return;
          if (!e.target.closest('.shopify-payment-button, [data-shopify="payment-button"]')) return;
          guard(e);
        },
        true
      );
    });

    refresh(false);
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
